import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';

import {
  AmbassadorStatusDto,
  Apps,
  ImageDto,
  ProfileDto,
  ProfilesClient,
  ProfilesDto, TokenMetadataDto, TokensClient,
  UpdateAddressDto
} from '../../api/wallet-api.generated';
import {AppSettingsService} from '../../shared/services/app-settings/app-settings.service';
import {AuthService} from '../../auth/auth.service/auth.service';
import {InviterData} from '../../shared/models/inviter-data.model';

@Injectable({
  providedIn: 'root',
})
export class ProfilesService {

  profilesSubject: BehaviorSubject<ProfileDto[]> = new BehaviorSubject<ProfileDto[]>(null);

  profiles$: Observable<ProfileDto[]> = this.profilesSubject.asObservable();

  inviterDataSubject: BehaviorSubject<InviterData> = new BehaviorSubject<InviterData>({});

  inviterData$: Observable<InviterData> = this.inviterDataSubject.asObservable();

  profileImageBasePath: string = AppSettingsService.profileImagesLocation + '/';

  defaultImgPath: string = AppSettingsService.profileImagesLocation + '/' + environment.defaultConsumerLogoId + '.PNG';

  private tokensMetadataSubject: BehaviorSubject<TokenMetadataDto[]> = new BehaviorSubject(null);

  constructor(authService: AuthService,
              private profilesClient: ProfilesClient,
              private tokensClient: TokensClient) {
  }

  get tokensMetadata$(): Observable<TokenMetadataDto[]> {
    return this.tokensMetadataSubject.pipe();
  }

  set tokensMetadata(value: TokenMetadataDto[]) {
    this.tokensMetadataSubject.next(value);
  }

  set profiles(profiles: ProfileDto[]) {
    this.profilesSubject.next(profiles);
  }

  set inviterData(data: ProfilesDto) {
    this.inviterDataSubject.next(this.getInviterData(data));
  }

  public getProfiles() {
    return this.profilesClient.getProfilesV2(Apps.Portal).pipe(
      tap((response) => {
        this.profiles = response?.profiles;
        this.inviterData = response;
      }),
      map((response) => response?.profiles),
    );
  }

  /*todo remove subscription*/
  public reloadProfiles() {
    return this.getProfiles().pipe(
      take(1),
    ).subscribe(() => {},
        err => this.profilesSubject.error(err));
  }

  public retrieveSelectedProfile(profileId: string): Observable<ProfileDto> {
    if (!this.profilesSubject.value?.length) {
      return this.getProfile(profileId);
    }

    return this.selectProfile(this.profilesSubject.value, profileId);
  }

  public createProfile(profile: ProfileDto): Observable<void> {
    return this.profilesClient.createProfile(profile);
  }

  public updateProfile(id: string, profile: ProfileDto): Observable<void> {
    return this.profilesClient.updateProfile(id, profile);
  }

  public getProfile(id: string): Observable<ProfileDto> {
    return this.profilesClient.getProfile(id);
  }

  public deleteProfile(id: string): Observable<boolean> {
    return this.profilesClient.deleteProfile(id);
  }

  public renameAddress(body: UpdateAddressDto): Observable<void> {
    return this.profilesClient.updateAddressV3(body);
  }

  public onImgError(event) {
    event.target.src = this.defaultImgPath;
  }

  public getImageUrl(image: ImageDto): string {
    if (image) {
      return `${this.profileImageBasePath}${image.id}.${image.mediaType}`;
    }

    return this.defaultImgPath;
  }

  public getAmbassadorStatus(profileId: string): Observable<AmbassadorStatusDto> {
    return this.profilesClient.ambassador(profileId);
  }

  public getSelectedProfile(profileId: string): ProfileDto {
    return this.profilesSubject?.value.find((profile) => profile.id === profileId);
  }

  public initTokensMetadata(): Observable<TokenMetadataDto[]> {
    return this.tokensClient.getTokensMetadata();
  }

  protected selectProfile(profiles: ProfileDto[], profileId: string): Observable<ProfileDto> {
    return this.profiles$.pipe(
      filter((data) => data?.length > 0),
      map((data) => data.find((profile) => profile.id === profileId)),
    );
  }

  private getInviterData(response: ProfilesDto): InviterData {
    return {
      inviterLegalName: response.inviterLegalName,
      inviterEmail: response.inviterEmail,
    };
  }
}
